import { type ReactNode, useEffect, useMemo } from "react";
import { Dialog } from "@headlessui/react";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  DecisionTree,
  PaneType,
  Tree,
} from "@unlikelyai-magic/shared/data-access/api";
import { Modal } from "@unlikelyai-magic/ui/modals";
import { LayoutFlowStatic, LayoutFlowStaticProps } from "./LayoutFlowStatic";

/**
 * A question-answer pair used for evaluating decision trees.
 */
export interface QuestionAnswer {
  question: string;
  value: unknown;
  reasoning?: string;
}

export interface GraphModalProps {
  /**
   * The tree to display in the modal.
   */
  tree: Tree;
  /**
   * Question-answer pairs for evaluating the tree.
   */
  questionAnswers?: QuestionAnswer[];
  /**
   * Callback when the modal is closed.
   */
  onClose: () => void;
  /**
   * Optional callback when a node is clicked.
   */
  onNodeClick?: LayoutFlowStaticProps["onNodeClick"];
  /**
   * Optional callback when the pane is clicked.
   */
  onPaneClick?: LayoutFlowStaticProps["onPaneClick"];
  /**
   * Optional brief explanation shown at the top of the modal.
   */
  explanation?: ReactNode;
}

/**
 * A modal that displays a decision tree graph visualization.
 */
export const GraphModal = ({
  tree,
  questionAnswers = [],
  onClose,
  onNodeClick,
  onPaneClick,
  explanation,
}: GraphModalProps) => {
  // Suppress ResizeObserver error from ReactFlow
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      const errorMessage = e.message || e.error?.message || "";
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    // Also handle unhandled promise rejections that might contain ResizeObserver errors
    const handleUnhandledRejection = (e: PromiseRejectionEvent) => {
      const errorMessage = e.reason?.message || String(e.reason || "");
      if (
        errorMessage.includes(
          "ResizeObserver loop completed with undelivered notifications"
        ) ||
        errorMessage.includes("ResizeObserver loop limit exceeded")
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Use capture phase to catch the error earlier
    window.addEventListener("error", handleError, true);
    window.addEventListener(
      "unhandledrejection",
      handleUnhandledRejection,
      true
    );

    return () => {
      window.removeEventListener("error", handleError, true);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
        true
      );
    };
  }, []);

  const panes = useMemo(() => {
    return [
      {
        paneType: PaneType.DEFINITION,
        definition: {
          rootNode: tree.template,
          name: tree.name,
          description: tree.description,
        } as DecisionTree,
        id: tree.id,
        explanation: tree.description,
        excerpt: tree.description,
      },
    ];
  }, [tree]);

  const layoutFlowStatic = useMemo(() => {
    return (
      <LayoutFlowStatic
        panes={panes}
        questionAnswers={questionAnswers}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
      />
    );
  }, [panes, questionAnswers, onNodeClick, onPaneClick]);

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 z-50 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <Modal>
          <Modal.Header title={tree.name} onClose={onClose} />
          <Modal.Content>
            {explanation && (
              <div style={{ padding: "16px 24px 0", fontSize: "18px", color: "#475569", textAlign: "center" }}>
                {explanation}
              </div>
            )}
            <div style={{ width: "90vw", height: "80vh", maxWidth: "1200px" }}>
              <ReactFlowProvider>{layoutFlowStatic}</ReactFlowProvider>
            </div>
          </Modal.Content>
        </Modal>
      </div>
    </Dialog>
  );
};
